import { useState } from "react";

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_EXTENSION = ".csv";
export const CSV_TEMPLATE_PATH = "/templates/users-template.csv";

/**
 * Validates a candidate users CSV file. Returns an error message string when the
 * file is invalid, or null when it passes (correct extension and within size limit).
 */
export const validateCsvFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(ACCEPTED_EXTENSION)) {
        return "Invalid file type. Please select a .csv file.";
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
        return `File size exceeds the maximum limit of ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
};

/**
 * Shared file-selection state for the bulk users CSV flows. Owns the selected file,
 * the validation/upload error list, and a key used to force-remount the UploadButton
 * after an invalid selection so the same file can be re-picked.
 *
 * Consumed by both the legacy single-step CsvUploadSheet and the multi-step
 * BulkUploadWithSteps wizard so validation behaviour stays in one place.
 */
export const useCsvFileSelection = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [uploadButtonKey, setUploadButtonKey] = useState(0);

    const handleFileSelection = (file: File) => {
        const validationError = validateCsvFile(file);
        if (validationError) {
            setErrors([validationError]);
            setSelectedFile(null);
            setUploadButtonKey((prev) => prev + 1);
            return;
        }

        setErrors([]);
        setSelectedFile(file);
    };

    const reset = () => {
        setSelectedFile(null);
        setErrors([]);
    };

    // setSelectedFile is intentionally not exposed: file selection must go through
    // handleFileSelection so validateCsvFile is the only path to setting a file.
    return {
        selectedFile,
        errors,
        setErrors,
        uploadButtonKey,
        handleFileSelection,
        reset
    };
};
